import type {
  LaunchNotifyEventDto,
  LaunchSecureDto,
  PatinfoProfileDto,
} from '../../types/api/patinfo';
import { setPhiValue } from '../../types/api/patinfo';
import type { AccountPlaDto, SettingDto } from '../../types/api';
import { buildLaunchPhi, type ClientProfileEditForm } from './ClientFormBuilder';

/** Origin (scheme + host) the backend should build the /secure/{SSKey} link against —
 * shared with ProfileRowEditor's own copy/paste link display, so both agree on the same
 * origin for a given launch. */
export const resolveCurrentBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
};

/** LaunchSecureDto.Status >= '2' inserts the launch. */
const LAUNCH_INSERT_STATUS = '2';

/** LaunchSecureDto.Status that cancels/deletes an existing launch at the backend. */
const LAUNCH_CANCEL_STATUS = '-1';

/**
 * LaunchNotifyEventDto.Status value that forces the notification to go out
 * immediately, bypassing scheduling.
 */
const NOTIFY_FORCE_SEND_STATUS = '2';

/** LaunchNotifyEventDto.Status value for a reminder waiting for its scheduled EDate. */
const NOTIFY_PENDING_STATUS = '0';

/** NotifyEventDto/LaunchNotifyEventDto.Type base code used for the "Send Now" email event. */
const HOWCONTACT_EMAIL_TYPE = '200';

// Email-flavored notify type codes map to their Text-flavored counterpart when the
// client's How Contact selection is Text/SMS — same event, different delivery channel.
const HOWCONTACT_TEXT_TYPE_MAP: Record<string, string> = {
  '200': '270',
  '201': '271',
};

const resolveNotifyType = (baseType: string, isTextHowContact: boolean): string => {
  if (!isTextHowContact) {
    return baseType;
  }
  return HOWCONTACT_TEXT_TYPE_MAP[baseType] ?? baseType;
};

const getRemoteInfoValue = (remoteInfo: SettingDto[] | undefined, name: string): string => {
  const target = name.trim().toLowerCase();
  return (remoteInfo ?? []).find((item) => item.Name.trim().toLowerCase() === target)?.Value ?? '';
};

/** 'YYYY-MM-DD' (local calendar date) -> ISO datetime at local midnight. */
const toMidnightIso = (dueDate: string): string => {
  return new Date(`${dueDate}T00:00:00`).toISOString();
};

/** DueDate plus the Pla's RemoteInfo `terminatedays`, same time-of-day as DueDate. */
const computeTerminateIso = (dueDateIso: string, terminateDaysRaw: string): string => {
  const terminateDays = parseInt(terminateDaysRaw, 10) || 0;
  const dueDateMs = new Date(dueDateIso).getTime();
  return new Date(dueDateMs + terminateDays * 24 * 60 * 60 * 1000).toISOString();
};

// Fixed (non-DST-aware) standard-time UTC offsets for the account-level Deliver Zone
// options (see TouchpointsView.tsx's DELIVER_ZONE_OPTIONS) — matches the level of rigor
// already used for these settings elsewhere in this codebase.
const DELIVER_ZONE_UTC_OFFSET_HOURS: Record<string, number> = {
  ET: -5,
  CT: -6,
  MT: -7,
  PT: -8,
  AKT: -9,
  HT: -10,
};

/**
 * A scheduled reminder's EDate: the due date at midnight, offset by the reminder's RDays
 * (days relative to the due date), landing at the Pla's RemoteInfo `delivertime` (minutes
 * since midnight) in its `deliverzone` timezone.
 */
const computeScheduledEventDate = (
  dueDate: string,
  rDaysRaw: string,
  delivertimeRaw: string,
  deliverzoneRaw: string
): string => {
  const [year, month, day] = dueDate.split('-').map((part) => parseInt(part, 10));
  const rDays = parseInt(rDaysRaw, 10) || 0;
  const minutesSinceMidnight = parseInt(delivertimeRaw, 10) || 0;
  const offsetHours = DELIVER_ZONE_UTC_OFFSET_HOURS[deliverzoneRaw] ?? 0;

  const utcMs = Date.UTC(year, month - 1, day + rDays, 0, minutesSinceMidnight, 0) - offsetHours * 60 * 60 * 1000;
  return new Date(utcMs).toISOString();
};

/**
 * Builds the launch_secure_put request for the "Send Now" action: inserts a new
 * secure assessment launch with a single NotifyState event set to force-send
 * immediately. LaunchSecureDto has no patcode/patientid field, so the profile's
 * patcode travels in the Phi list (matching how the backend already correlates
 * profile identity through Phi elsewhere). Phi is built from the current form state
 * (not just profile.phi) so fname/lname/patid/email/phone/etc. reflect what's on
 * screen even if the user hasn't hit Update yet.
 */
export function buildSendNowLaunchRequest(
  profile: PatinfoProfileDto,
  form: ClientProfileEditForm,
  assessment: AccountPlaDto,
  isTextHowContact: boolean
): LaunchSecureDto {
  const now = new Date().toISOString();
  const phi = setPhiValue(buildLaunchPhi(profile, form), 'patcode', profile.patcode);

  const notifyEvent: LaunchNotifyEventDto = {
    Type: resolveNotifyType(HOWCONTACT_EMAIL_TYPE, isTextHowContact),
    EDate: now,
    Sent: '',
    Status: NOTIFY_FORCE_SEND_STATUS,
  };

  return {
    SSKey: '',
    AssessmentId: assessment.AssessmentId ?? '',
    DName: assessment.DName,
    BaseURL: resolveCurrentBaseUrl(),
    Phi: phi,
    UVars: assessment.UVars ?? [],
    Preloads: assessment.Preloads ?? [],
    Extra: [],
    NotifyState: [notifyEvent],
    DueDate: now,
    Terminate: computeTerminateIso(now, getRemoteInfoValue(assessment.RemoteInfo, 'terminatedays')),
    Status: LAUNCH_INSERT_STATUS,
  };
}

/**
 * Builds the launch_secure_put request for the "Schedule" action (manual scheduling):
 * same insert shape as Send Now, but DueDate comes from the user-entered due date instead
 * of now, and NotifyState is the Pla's full NotifySched reminder list (each landing at its
 * configured RDays/delivertime/deliverzone) rather than a single force-send event.
 */
export function buildScheduleLaunchRequest(
  profile: PatinfoProfileDto,
  form: ClientProfileEditForm,
  assessment: AccountPlaDto,
  dueDate: string,
  isTextHowContact: boolean
): LaunchSecureDto {
  const phi = setPhiValue(buildLaunchPhi(profile, form), 'patcode', profile.patcode);
  const dueDateIso = toMidnightIso(dueDate);
  const delivertime = getRemoteInfoValue(assessment.RemoteInfo, 'delivertime');
  const deliverzone = getRemoteInfoValue(assessment.RemoteInfo, 'deliverzone');

  const notifyState: LaunchNotifyEventDto[] = (assessment.NotifySched ?? []).map((event) => ({
    Type: resolveNotifyType(event.NType, isTextHowContact),
    EDate: computeScheduledEventDate(dueDate, event.RDays, delivertime, deliverzone),
    Sent: '',
    Status: NOTIFY_PENDING_STATUS,
  }));

  return {
    SSKey: '',
    AssessmentId: assessment.AssessmentId ?? '',
    DName: assessment.DName,
    BaseURL: resolveCurrentBaseUrl(),
    Phi: phi,
    UVars: assessment.UVars ?? [],
    Preloads: assessment.Preloads ?? [],
    Extra: [],
    NotifyState: notifyState,
    DueDate: dueDateIso,
    Terminate: computeTerminateIso(dueDateIso, getRemoteInfoValue(assessment.RemoteInfo, 'terminatedays')),
    Status: LAUNCH_INSERT_STATUS,
  };
}

/**
 * Builds the launch_secure_put request for the "Cancel" action: resends the
 * existing launch (identified by its backend-assigned SSKey) with Status set
 * to the cancel/delete sentinel. On success the caller should remove this
 * launch from the profile's `launches` locally.
 */
export function buildCancelLaunchRequest(launch: LaunchSecureDto): LaunchSecureDto {
  return {
    ...launch,
    Status: LAUNCH_CANCEL_STATUS,
  };
}
